import React, { useState, useEffect, useRef } from 'react'
import { DateTime } from 'luxon'
import MonitorCard from 'components/Dashboard/MonitorCard'
import StatCards from 'components/Dashboard/StatCards'
import TableComponent from 'components/common/TableComponent'
import { useTableData } from 'hooks/useTableData'
import { makeRequest } from 'services/request.service'
import { useAuth } from 'auth/AuthContext'

// GET request for packing summary data
const getPackingSummaryData = async (farmId, filterDate) => {
  try {
    const response = await makeRequest({
      method: 'GET',
      url: `/dashboard/${farmId}/packing-summary?filterDate=${new Date(filterDate).toISOString()}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      }
    })
    return response.data
  } catch (error) {
    console.error('Error fetching packing summary data:', error)
    throw error
  }
}

const themeColorMap = (shade) => {
  return {
    700: 'bg-green-700',
    600: 'bg-green-600',
    500: 'bg-green-500',
    400: 'bg-green-400'
  }[shade]
}

const DailyPackingSummary = () => {
  const { user } = useAuth()
  const hasFetched = useRef(false)

  // Get current date in Mexico City timezone for date picker
  const currentDateForPicker = DateTime.now().setZone('America/Mexico_City').toFormat('yyyy-MM-dd')

  // New state for selected date
  const [selectedDate, setSelectedDate] = useState(currentDateForPicker)

  // State for API data
  const [packingSummaryData, setPackingSummaryData] = useState({
    dailyPackingSummary: {
      totalPacked: 0,
      packerCount: 0,
      averagePerWorker: 0,
      averagePackedPerHour: 0
    },
    dailyPackingSummarySameHourPreviousDay: {
      totalPacked: 0,
      packerCount: 0
    },
    packerGroupsMonitor: [],
    packersMonitor: [],
    loading: false,
    error: null
  })

  const fetchPackingSummaryData = async (dateToFetch = selectedDate) => {
    if (!user?.farmId) return

    try {
      setPackingSummaryData(prev => ({ ...prev, loading: true, error: null }))

      const data = await getPackingSummaryData(user.farmId, dateToFetch)

      setPackingSummaryData(prev => ({
        ...prev,
        dailyPackingSummary: data.dailyPackingSummary || prev.dailyPackingSummary,
        dailyPackingSummarySameHourPreviousDay: data.dailyPackingSummarySameHourPreviousDay || prev.dailyPackingSummarySameHourPreviousDay,
        packerGroupsMonitor: data.packerGroupsMonitor || [],
        packersMonitor: data.packersMonitor || [],
        loading: false
      }))
    } catch (error) {
      console.error('Error fetching packing summary:', error)
      setPackingSummaryData(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load packing summary data'
      }))
    }
  }

  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true

    fetchPackingSummaryData()
  }, [user?.farmId])

  // Refetch when selectedDate changes
  useEffect(() => {
    if (!hasFetched.current) return
    fetchPackingSummaryData(selectedDate)
  }, [selectedDate])

  // Create cards data from API response
  const createCardsData = () => {
    const { dailyPackingSummary, dailyPackingSummarySameHourPreviousDay } = packingSummaryData
    const selectedDateFormatted = DateTime.fromISO(selectedDate).toFormat('dd.MM.yyyy')

    return [
      {
        title: 'Total Packed',
        value: Number(dailyPackingSummary.totalPacked || 0).toLocaleString(),
        unit: 'Units',
        sub: `Last Packing Day (${selectedDateFormatted})`,
        color: 'bg-green-700'
      },
      {
        title: 'Total Packed',
        value: Number(dailyPackingSummarySameHourPreviousDay.totalPacked || 0).toLocaleString(),
        unit: 'Units',
        sub: 'Same Hour On Previous Packing Day',
        color: themeColorMap(500)
      },
      {
        title: 'Packers Count',
        value: Number(dailyPackingSummary.packerCount || 0).toString(),
        unit: 'Workers',
        sub: `Last Packing Day (${selectedDateFormatted})`,
        color: 'bg-green-700'
      },
      {
        title: 'Packers Count',
        value: Number(dailyPackingSummarySameHourPreviousDay.packerCount || 0).toString(),
        unit: 'Workers',
        sub: 'Same Hour On Previous Packing Day',
        color: themeColorMap(500)
      },
      {
        title: 'Avg Per Packer',
        value: Number(dailyPackingSummary.averagePerWorker || 0).toFixed(2),
        unit: 'Units',
        sub: `Last Packing Day (${selectedDateFormatted})`,
        color: 'bg-green-600'
      },
      {
        title: 'Worker Avg',
        value: Number(dailyPackingSummary.averagePackedPerHour || 0).toFixed(2),
        unit: 'Units/Hour',
        sub: `Last Packing Day (${selectedDateFormatted})`,
        color: 'bg-green-700'
      }
    ]
  }

  // Use the custom hook to generate table data with totals
  const packerGroupsTableData = useTableData(packingSummaryData.packerGroupsMonitor, 'packerGroups')
  const packerMonitorTableData = useTableData(packingSummaryData.packersMonitor, 'packerMonitor')

  if (packingSummaryData.loading) {
    return (
      <div className='p-6 bg-gray-50 min-h-screen'>
        <div className='flex justify-center items-center h-64'>
          <div className='text-lg text-gray-600'>Loading packing summary...</div>
        </div>
      </div>
    )
  }

  if (packingSummaryData.error) {
    return (
      <div className='p-6 bg-gray-50 min-h-screen'>
        <div className='flex justify-center items-center h-64'>
          <div className='text-lg text-red-600'>{packingSummaryData.error}</div>
        </div>
      </div>
    )
  }

  return (
    <div className='p-6 bg-gray-50 min-h-screen'>

      <div className='flex justify-between items-start mb-4'>
        <div>
          <h1 className='text-2xl font-semibold text-green-600'>Daily Packing Summary</h1>
          <p className='text-sm text-gray-600'>
            Change Filters
          </p>
        </div>
        {/* Date Picker */}
        <div className='flex items-center gap-2'>
          <label htmlFor='pack-date' className='text-sm text-gray-700'>Select Date:</label>
          <input
            id='pack-date'
            type='date'
            className='border rounded px-2 py-1 text-sm'
            value={selectedDate}
            max={currentDateForPicker}
            onChange={e => setSelectedDate(e.target.value)}
          />
        </div>
      </div>

      <hr className='border-t border-gray-300 mb-6' />

      <StatCards cards={createCardsData()} />

      <MonitorCard title='Packer Groups Monitor' date={DateTime.fromISO(selectedDate).toFormat('dd.MM.yyyy')} hasData={packerGroupsTableData.rows.length > 0} color="green">
        <TableComponent tableData={packerGroupsTableData} defaultSortColumn={packerGroupsTableData.columns[0]?.key} />
      </MonitorCard>

      <MonitorCard title='Packer Monitor' date={DateTime.fromISO(selectedDate).toFormat('dd.MM.yyyy')} hasData={packerMonitorTableData.rows.length > 0} color="green">
        <TableComponent tableData={packerMonitorTableData} defaultSortColumn={packerMonitorTableData.columns[0]?.key} />
      </MonitorCard>
    </div>
  )
}

export default DailyPackingSummary
